import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    useGoogleAddressAutocomplete,
    type GoogleAddressResult,
} from "../hooks/useGoogleAddressAutocomplete";
import {
    adminEditOrder,
    adjustPaidOrder,
    fetchOrders,
    previewAdminEditOrder,
    setOrderDiscount,
    updateOrderDetails,
    type Order,
    type OrderAdjustmentSettlement,
} from "../api/orderApi";
import {
    searchOrderProducts,
    type OrderProductSearchResult,
} from "../api/orderProductSearchApi";
import AttachCustomerControl from "../components/orders/AttachCustomerControl";
import NumberField from "../components/NumberField";
import { formatPhoneInput } from "../utils/phone";

type FulfillmentMethod = "shipping" | "local_delivery";

// Promotion/coupon "free reward" lines are driven by the promo engine — their
// price is never manually editable (mirrors the backend guard).
function isPromoFreeItem(item: any) {
    return (
        Number(item?.price || 0) === 0 &&
        String(item?.variant_name || "")
            .toLowerCase()
            .includes("free promotional item")
    );
}

export default function OrderEditPage() {
    const { orderId } = useParams();
    const navigate = useNavigate();

    const numericOrderId = Number(orderId);

    const [order, setOrder] = useState<Order | null>(null);
    const [draftItems, setDraftItems] = useState<any[]>([]);
    const [fulfillmentMethod, setFulfillmentMethod] =
        useState<FulfillmentMethod>("shipping");

    const [customerDraft, setCustomerDraft] = useState({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        shipping_address: "",
        shipping_address2: "",
        shipping_city: "",
        shipping_state: "",
        shipping_zip_code: "",
        note: "",
    });

    const [search, setSearch] = useState("");
    const [searchResults, setSearchResults] = useState<OrderProductSearchResult[]>([]);
    const [searching, setSearching] = useState(false);
    const [priceEditReason, setPriceEditReason] = useState("");

    // Google Places autocomplete on the shipping address, mirroring the create-
    // order page. Selecting a suggestion fills street/city/province/postal.
    const addressInputRef = useRef<HTMLInputElement | null>(null);
    const handleAddressSelect = useCallback((addr: GoogleAddressResult) => {
        setCustomerDraft((prev) => ({
            ...prev,
            shipping_address: addr.street || addr.formatted,
            shipping_city: addr.city,
            shipping_state: addr.state,
            shipping_zip_code: addr.postalCode,
        }));
    }, []);
    useGoogleAddressAutocomplete(addressInputRef, handleAddressSelect);

    // Order-level discount / coupon editor (applied immediately, separate from
    // the item-edit save).
    const [discKind, setDiscKind] = useState<"none" | "percentage" | "fixed" | "coupon">("none");
    const [discValue, setDiscValue] = useState<number | "">("");
    const [discCoupon, setDiscCoupon] = useState("");
    const [applyingDiscount, setApplyingDiscount] = useState(false);

    // Paid-order adjustment settlement choices
    const [refundDestination, setRefundDestination] = useState<"" | "manual" | "store_credit">("");
    const [collectNow, setCollectNow] = useState(false);
    const [collectMethod, setCollectMethod] = useState("cash");

    const [preview, setPreview] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [previewing, setPreviewing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const loadOrder = async () => {
        setLoading(true);
        setError("");

        try {
            const orders = await fetchOrders({ limit: 500 });
            const found = orders.find((o) => o.order_id === numericOrderId);

            if (!found) {
                setError("Order not found.");
                return;
            }

            setOrder(found);
            setDraftItems(found.items.map((item) => ({ ...item })));
            setFulfillmentMethod(found.is_delivery ? "local_delivery" : "shipping");

            // Seed the discount editor from the order's current discount/coupon.
            if ((found as any).coupon_code) {
                setDiscKind("coupon");
                setDiscCoupon((found as any).coupon_code);
                setDiscValue("");
            } else if (found.discount_type && Number(found.discount_value) > 0) {
                const dt = String(found.discount_type).toLowerCase();
                setDiscKind(dt === "percent" || dt === "percentage" ? "percentage" : "fixed");
                setDiscValue(Number(found.discount_value));
                setDiscCoupon("");
            } else {
                setDiscKind("none");
                setDiscValue("");
                setDiscCoupon("");
            }
            // The address is stored as "street\napartment" (see checkout's
            // payloadBuilder + admin create-order). Split it back into two
            // fields for editing, then re-compose on save.
            const rawShippingAddress = found.shipping_address?.address || "";
            const addrNlIdx = rawShippingAddress.indexOf("\n");
            setCustomerDraft({
                customer_name: found.customer_name || "",
                customer_email: found.customer_email || "",
                customer_phone: found.customer_phone || "",
                shipping_address: addrNlIdx >= 0 ? rawShippingAddress.slice(0, addrNlIdx) : rawShippingAddress,
                shipping_address2: addrNlIdx >= 0 ? rawShippingAddress.slice(addrNlIdx + 1) : "",
                shipping_city: found.shipping_address?.city || "",
                shipping_state: found.shipping_address?.state || "",
                shipping_zip_code: found.shipping_address?.zip_code || "",
                note: found.note || "",
            });
        } catch (err: any) {
            setError(err?.response?.data?.error || "Failed to load order.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!numericOrderId) {
            setError("Invalid order id.");
            setLoading(false);
            return;
        }

        loadOrder();
    }, [numericOrderId]);

    const payload = useMemo(() => {
        if (!order) return null;

        const isPaid =
            order.payment_status === "paid" ||
            Number(order.payment_balance?.paidTotal || 0) > 0;

        // Re-compose street + apartment into the single shipping_address the
        // backend stores (matches checkout / admin create-order), and don't
        // send the UI-only shipping_address2 helper field.
        const { shipping_address2, ...restCustomer } = customerDraft;
        const composedShippingAddress = shipping_address2?.trim()
            ? `${customerDraft.shipping_address}\n${shipping_address2.trim()}`
            : customerDraft.shipping_address;

        return {
            fulfillment_method: fulfillmentMethod,
            items: draftItems.map((item) => ({
                id: typeof item.id === "number" ? item.id : undefined,
                product_id: item.product_id,
                variant_id: item.variant_id || null,
                quantity: Number(item.quantity || 1),
                price: Number(item.price || 0),
            })),
            customer: { ...restCustomer, shipping_address: composedShippingAddress },
            price_edit_reason: priceEditReason.trim() || null,
            // Adjustment mode: preview must reflect price overrides for paid orders.
            adjust: isPaid,
        };
    }, [order, draftItems, fulfillmentMethod, customerDraft, priceEditReason]);

    useEffect(() => {
        if (!order || !payload || draftItems.length === 0) return;

        const timer = window.setTimeout(async () => {
            try {
                setPreviewing(true);
                const res = await previewAdminEditOrder(order.order_id, payload);
                setPreview(res.preview);
            } catch (err: any) {
                setPreview(null);
                setError(err?.response?.data?.error || "Failed to preview order edit.");
            } finally {
                setPreviewing(false);
            }
        }, 350);

        return () => window.clearTimeout(timer);
    }, [order, payload, draftItems.length]);

    const handleSearch = async (value: string) => {
        setSearch(value);

        if (!order || value.trim().length < 2) {
            setSearchResults([]);
            return;
        }

        setSearching(true);
        setError("");

        try {
            const results = await searchOrderProducts(order.order_id, value);
            setSearchResults(results);
        } catch (err: any) {
            setError(err?.response?.data?.error || "Failed to search products.");
        } finally {
            setSearching(false);
        }
    };

    // Price to show/seed for a picker variant: the WHOLESALE price when this is a
    // wholesale order and one is configured, else retail. The backend recomputes
    // the authoritative line price (incl. quantity breaks) on preview/save.
    const isWholesaleOrder = !!order?.is_wholesale;
    const pickerPriceFor = (v?: { price?: number; wholesale_price?: number | null }) =>
        Number((isWholesaleOrder && v?.wholesale_price != null ? v.wholesale_price : v?.price) || 0);

    const handleAddVariant = (
        product: OrderProductSearchResult,
        variantId: number | null
    ) => {
        const variant = (product.variants || []).find((v) => v.id === variantId);

        setDraftItems((prev) => [
            ...prev,
            {
                id: `temp-${product.product_id}-${variantId || "standard"}-${Date.now()}`,
                product_id: product.product_id,
                variant_id: variantId,
                product_name: product.product_name,
                variant_name: variant?.variant_name || "Standard",
                image_url: variant?.image_url || product.product_image || undefined,
                quantity: 1,
                price: pickerPriceFor(variant),
                attributes: variant?.attributes || [],
            },
        ]);

        setSearch("");
        setSearchResults([]);
    };

    const duplicateDraftItem = (item: any) => {
        setDraftItems((prev) => {
            const idx = prev.findIndex((x) => x.id === item.id);
            const copy = {
                ...item,
                id: `temp-dup-${item.product_id}-${item.variant_id || "std"}-${Date.now()}`,
                quantity: 1,
            };
            const next = [...prev];
            next.splice(idx + 1, 0, copy);
            return next;
        });
    };

    // Apply/replace/clear the order-level discount or coupon immediately, then
    // refresh the order's discount fields (leaves unsaved item edits intact).
    const applyDiscount = async () => {
        if (!order) return;
        setApplyingDiscount(true);
        setError("");
        try {
            let payload: any;
            if (discKind === "coupon") payload = { coupon_code: discCoupon.trim() };
            else if (discKind === "percentage" || discKind === "fixed")
                payload = { discount: { type: discKind, value: Number(discValue || 0) } };
            else payload = { clear: true };

            const res = await setOrderDiscount(order.order_id, payload);
            const updated = res?.order || {};
            setOrder((prev) =>
                prev
                    ? {
                          ...prev,
                          discount_type: updated.discount_type ?? null,
                          discount_value: Number(updated.discount_value || 0),
                          coupon_code: updated.coupon_code ?? null,
                      }
                    : prev
            );
        } catch (err: any) {
            setError(err?.response?.data?.error || "Failed to apply discount.");
        } finally {
            setApplyingDiscount(false);
        }
    };

    const handleSave = async () => {
        if (!order || !payload) return;

        if (draftItems.length === 0) {
            setError("Order must have at least one item.");
            return;
        }

        const isPaid =
            order.payment_status === "paid" ||
            Number(order.payment_balance?.paidTotal || 0) > 0;
        const bal = preview?.paymentBalance;
        const refDue = Number(bal?.refundDue || 0);
        const balDue = Number(bal?.balanceDue || 0);

        if (isPaid && refDue > 0 && !refundDestination) {
            setError("Choose how to refund the difference (original method or store credit).");
            return;
        }

        setSaving(true);
        setError("");

        try {
            if (isPaid) {
                const settlement: OrderAdjustmentSettlement = {};
                if (refDue > 0) {
                    settlement.refund_destination = refundDestination as "manual" | "store_credit";
                }
                if (balDue > 0 && collectNow) {
                    settlement.collect = { amount: balDue, method: collectMethod };
                }
                await adjustPaidOrder(order.order_id, {
                    fulfillment_method: fulfillmentMethod,
                    items: draftItems.map((item) => ({
                        id: typeof item.id === "number" ? item.id : undefined,
                        product_id: item.product_id,
                        variant_id: item.variant_id || null,
                        quantity: Number(item.quantity || 1),
                        price: Number(item.price || 0),
                    })),
                    settlement,
                    reason: priceEditReason.trim() || null,
                });

                // adjustPaidOrder only handles items/settlement — persist any
                // contact/shipping edits separately (no payment guard on this
                // endpoint). The unpaid path already saves these via adminEditOrder.
                const composedShippingAddress = customerDraft.shipping_address2?.trim()
                    ? `${customerDraft.shipping_address}\n${customerDraft.shipping_address2.trim()}`
                    : customerDraft.shipping_address;
                await updateOrderDetails(order.order_id, {
                    customer_name: customerDraft.customer_name,
                    customer_email: customerDraft.customer_email,
                    customer_phone: customerDraft.customer_phone,
                    shipping_address: composedShippingAddress,
                    shipping_city: customerDraft.shipping_city,
                    shipping_state: customerDraft.shipping_state,
                    shipping_zip_code: customerDraft.shipping_zip_code,
                    note: customerDraft.note,
                });
            } else {
                await adminEditOrder(order.order_id, payload);
            }
            navigate("/orders");
        } catch (err: any) {
            setError(err?.response?.data?.error || "Failed to update order.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!order) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error || "Order not found."}</Alert>
            </Box>
        );
    }

    const balance = preview?.paymentBalance;

    // A paid order goes through the adjustment flow: items/prices can change,
    // and the resulting delta is settled (refund or additional collection).
    const orderPaid =
        order.payment_status === "paid" ||
        Number(order.payment_balance?.paidTotal || 0) > 0;

    // Prices are editable when nothing has been collected (unpaid) or in the
    // paid-order adjustment flow.
    const canEditPrice = orderPaid
        ? true
        : preview && typeof preview.priceEditAllowed === "boolean"
            ? preview.priceEditAllowed
            : order.payment_status !== "paid" &&
              Number(order.payment_balance?.paidTotal || 0) === 0;

    // Settlement figures for the adjustment (from the recomputed preview).
    const refundDue = Number(balance?.refundDue || 0);
    const balanceDue = Number(balance?.balanceDue || 0);
    // A refund adjustment needs a destination chosen before it can be saved.
    const needsRefundChoice = orderPaid && refundDue > 0 && !refundDestination;

    return (
      <Box sx={{ bgcolor: "background.default", minHeight: "100vh", py: { xs: 2, md: 4 }, overflowX: "hidden" }}>
        <Box
            sx={{
                maxWidth: 1180,
                width: "100%",
                mx: "auto",
                px: { xs: 2, md: 3 },
                "& .MuiCard-root": {
                    borderRadius: 3,
                    border: "1px solid", borderColor: "divider",
                    boxShadow: "none",
                    bgcolor: "background.paper",
                },
                "& .MuiCardContent-root": { p: { xs: 2, md: 2.5 } },
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                <IconButton
                    onClick={() => navigate("/orders")}
                    sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", "&:hover": { bgcolor: "background.paper" } }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                        <Typography variant="h5" fontWeight={800}>
                            Order #{order.order_number}
                        </Typography>
                        <Chip
                            size="small"
                            label={String(order.payment_status || "pending").toUpperCase()}
                            color={order.payment_status === "paid" ? "success" : "warning"}
                            variant="outlined"
                            sx={{ height: 22, fontWeight: 700 }}
                        />
                        <Chip
                            size="small"
                            label={fulfillmentMethod === "local_delivery" ? "Local delivery" : "Shipping"}
                            variant="outlined"
                            sx={{ height: 22 }}
                        />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        Edit items, prices and fulfillment — totals recalculate live.
                    </Typography>
                </Box>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {orderPaid && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    This order has been paid. Editing it creates an <strong>adjustment</strong>:
                    lowering the total refunds the difference (to the original method or as
                    store credit); raising it lets you collect more. Sales stats and loyalty
                    are corrected automatically.
                </Alert>
            )}

            <Grid container spacing={2.5}>
                <Grid item xs={12} md={8}>
                    <Stack spacing={2.5}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
                                    Fulfillment
                                </Typography>

                                <TextField
                                    select
                                    fullWidth
                                    label="Fulfillment method"
                                    value={fulfillmentMethod}
                                    onChange={(e) =>
                                        setFulfillmentMethod(e.target.value as FulfillmentMethod)
                                    }
                                    disabled={saving}
                                >
                                    <MenuItem value="shipping">Shipping</MenuItem>
                                    <MenuItem value="local_delivery">Local Delivery</MenuItem>
                                </TextField>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent>
                                <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
                                    Customer / Address
                                </Typography>

                                <AttachCustomerControl
                                    orderId={order.order_id}
                                    currentCustomerId={order.customer_id}
                                    currentName={order.customer_name}
                                    currentEmail={order.customer_email}
                                    disabled={saving}
                                    onAttached={loadOrder}
                                />

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Customer"
                                            value={customerDraft.customer_name}
                                            disabled={saving}
                                            onChange={(e) =>
                                                setCustomerDraft((prev) => ({ ...prev, customer_name: e.target.value }))
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            value={customerDraft.customer_email}
                                            disabled={saving}
                                            onChange={(e) =>
                                                setCustomerDraft((prev) => ({ ...prev, customer_email: e.target.value }))
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Phone"
                                            type="tel"
                                            inputProps={{ inputMode: "tel" }}
                                            value={customerDraft.customer_phone}
                                            disabled={saving}
                                            onChange={(e) =>
                                                setCustomerDraft((prev) => ({ ...prev, customer_phone: formatPhoneInput(e.target.value) }))
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Address"
                                            value={customerDraft.shipping_address}
                                            disabled={saving}
                                            inputRef={addressInputRef}
                                            onChange={(e) =>
                                                setCustomerDraft((prev) => ({ ...prev, shipping_address: e.target.value }))
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Apartment, Suite, Unit (optional)"
                                            value={customerDraft.shipping_address2}
                                            disabled={saving}
                                            onChange={(e) =>
                                                setCustomerDraft((prev) => ({ ...prev, shipping_address2: e.target.value }))
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label="City"
                                            value={customerDraft.shipping_city}
                                            disabled={saving}
                                            onChange={(e) =>
                                                setCustomerDraft((prev) => ({ ...prev, shipping_city: e.target.value }))
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label="Province"
                                            value={customerDraft.shipping_state}
                                            disabled={saving}
                                            onChange={(e) =>
                                                setCustomerDraft((prev) => ({ ...prev, shipping_state: e.target.value }))
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label="Postal code"
                                            value={customerDraft.shipping_zip_code}
                                            disabled={saving}
                                            onChange={(e) =>
                                                setCustomerDraft((prev) => ({ ...prev, shipping_zip_code: e.target.value }))
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={3}
                                            label="Order note"
                                            value={customerDraft.note}
                                            disabled={saving}
                                            onChange={(e) =>
                                                setCustomerDraft((prev) => ({ ...prev, note: e.target.value }))
                                            }
                                        />
                                    </Grid>
                                </Grid>

                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent>
                                <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
                                    Items
                                </Typography>

                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Add product"
                                    placeholder="Search products..."
                                    value={search}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    disabled={saving}
                                />

                                {searching && (
                                    <Box sx={{ mt: 1 }}>
                                        <CircularProgress size={20} />
                                    </Box>
                                )}

                                {searchResults.length > 0 && (
                                    <Stack spacing={1} sx={{ mt: 1.5, mb: 2 }}>
                                        {searchResults.map((product) => (
                                            <Box
                                                key={product.product_id}
                                                sx={{
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    borderRadius: 2,
                                                    p: 1.5,
                                                }}
                                            >
                                                <Typography fontWeight={800}>{product.product_name}</Typography>

                                                <Stack spacing={1} sx={{ mt: 1 }}>
                                                    {(product.variants || []).map((variant) => (
                                                        <Box
                                                            key={variant.id}
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 1.5,
                                                            }}
                                                        >
                                                            <Avatar
                                                                src={variant.image_url || product.product_image || undefined}
                                                                variant="rounded"
                                                                sx={{ width: 42, height: 42 }}
                                                            />

                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography variant="body2" fontWeight={700} noWrap>
                                                                    {variant.variant_name || "Standard"}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    ${pickerPriceFor(variant).toFixed(2)}
                                                                    {isWholesaleOrder && variant.wholesale_price != null ? " (wholesale)" : ""}
                                                                    {typeof variant.available_stock !== "undefined"
                                                                        ? ` • Stock: ${variant.available_stock}`
                                                                        : ""}
                                                                </Typography>
                                                            </Box>

                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                startIcon={<AddIcon />}
                                                                onClick={() => handleAddVariant(product, variant.id)}
                                                                disabled={saving}
                                                            >
                                                                Add
                                                            </Button>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        ))}
                                    </Stack>
                                )}

                                <Stack spacing={1.25} sx={{ mt: 2 }}>
                                    {draftItems.map((item) => (
                                        <Box
                                            key={item.id}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                p: 1.5,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                borderRadius: 2,
                                                flexWrap: { xs: "wrap", sm: "nowrap" },
                                            }}
                                        >
                                            <Avatar
                                                src={item.image_url}
                                                variant="rounded"
                                                sx={{ width: 48, height: 48, flexShrink: 0 }}
                                            />

                                            <Box sx={{ flex: 1, minWidth: 0, width: { xs: "55%", sm: "auto" } }}>
                                                <Typography fontWeight={700} noWrap>
                                                    {item.product_name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                    {item.variant_name || "Standard"}
                                                </Typography>
                                            </Box>

                                            {canEditPrice && !isPromoFreeItem(item) ? (
                                                <NumberField
                                                    label="Price"
                                                    size="small"
                                                    value={item.price ?? 0}
                                                    disabled={saving}
                                                    min={0}
                                                    clampEmptyTo={0}
                                                    onValueChange={(v) =>
                                                        setDraftItems((prev) =>
                                                            prev.map((x) =>
                                                                x.id === item.id ? { ...x, price: v ?? 0 } : x
                                                            )
                                                        )
                                                    }
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">$</InputAdornment>
                                                        ),
                                                    }}
                                                    inputProps={{ step: "0.01" }}
                                                    sx={{ width: 120, flexShrink: 0 }}
                                                />
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                    sx={{ width: 90, textAlign: "right", flexShrink: 0 }}
                                                >
                                                    ${Number(item.price || 0).toFixed(2)}
                                                </Typography>
                                            )}

                                            <NumberField
                                                label="Qty"
                                                size="small"
                                                value={item.quantity}
                                                min={1}
                                                clampEmptyTo={1}
                                                sx={{ width: 80, flexShrink: 0 }}
                                                disabled={saving}
                                                onValueChange={(v) =>
                                                    setDraftItems((prev) =>
                                                        prev.map((x) =>
                                                            x.id === item.id ? { ...x, quantity: v ?? x.quantity } : x
                                                        )
                                                    )
                                                }
                                            />

                                            {canEditPrice && !isPromoFreeItem(item) && (
                                                <Tooltip title="Duplicate line (e.g. add a free replacement)">
                                                    <IconButton
                                                        disabled={saving}
                                                        sx={{ flexShrink: 0 }}
                                                        onClick={() => duplicateDraftItem(item)}
                                                    >
                                                        <ContentCopyIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <IconButton
                                                color="error"
                                                disabled={saving}
                                                sx={{ flexShrink: 0 }}
                                                onClick={() =>
                                                    setDraftItems((prev) => prev.filter((x) => x.id !== item.id))
                                                }
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Stack>

                                {canEditPrice ? (
                                    <TextField
                                        label="Reason for price change (optional)"
                                        size="small"
                                        fullWidth
                                        value={priceEditReason}
                                        disabled={saving}
                                        onChange={(e) => setPriceEditReason(e.target.value)}
                                        sx={{ mt: 2 }}
                                    />
                                ) : (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mt: 2, display: "block" }}
                                    >
                                        Line prices can only be edited before any payment is collected.
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Stack>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card sx={{ position: { md: "sticky" }, top: { md: 88 } }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6" fontWeight={800}>
                                    Order summary
                                </Typography>
                                {previewing && <CircularProgress size={18} />}
                            </Stack>

                            <Divider sx={{ my: 2 }} />

                            {/* Order-level discount / coupon (mutually exclusive) */}
                            {canEditPrice && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                        Discount / coupon
                                    </Typography>
                                    <Stack spacing={1}>
                                        <TextField
                                            select
                                            size="small"
                                            label="Discount"
                                            value={discKind}
                                            onChange={(e) => {
                                                setDiscKind(e.target.value as any);
                                                setDiscValue("");
                                                setDiscCoupon("");
                                            }}
                                            fullWidth
                                        >
                                            <MenuItem value="none">No discount</MenuItem>
                                            <MenuItem value="percentage">Percentage off (%)</MenuItem>
                                            <MenuItem value="fixed">Amount off ($)</MenuItem>
                                            <MenuItem value="coupon">Coupon code</MenuItem>
                                        </TextField>

                                        {(discKind === "percentage" || discKind === "fixed") && (
                                            <TextField
                                                size="small"
                                                type="number"
                                                label={discKind === "percentage" ? "Percent off" : "Amount off"}
                                                value={discValue}
                                                onChange={(e) =>
                                                    setDiscValue(e.target.value === "" ? "" : Number(e.target.value))
                                                }
                                                inputProps={{ min: 0, step: discKind === "percentage" ? 1 : 0.01 }}
                                                InputProps={
                                                    discKind === "percentage"
                                                        ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                                                        : { startAdornment: <InputAdornment position="start">$</InputAdornment> }
                                                }
                                                fullWidth
                                            />
                                        )}

                                        {discKind === "coupon" && (
                                            <TextField
                                                size="small"
                                                label="Coupon code"
                                                value={discCoupon}
                                                onChange={(e) => setDiscCoupon(e.target.value.toUpperCase())}
                                                fullWidth
                                            />
                                        )}

                                        <Button
                                            variant="outlined"
                                            size="small"
                                            disabled={applyingDiscount}
                                            onClick={applyDiscount}
                                        >
                                            {applyingDiscount
                                                ? "Applying…"
                                                : discKind === "none"
                                                ? "Remove discount"
                                                : "Apply discount"}
                                        </Button>
                                    </Stack>
                                </Box>
                            )}

                            <Stack spacing={1}>
                                <SummaryRow label="Subtotal" value={preview?.subtotal} />
                                <SummaryRow label="Discount" value={-(preview?.discountAmount || 0)} />
                                <SummaryRow
                                    label="Loyalty"
                                    value={-(preview?.loyaltyUsed ?? order.loyalty_points_used ?? 0)}
                                />
                                <SummaryRow label="Tax" value={preview?.tax} />
                                <SummaryRow
                                    label={fulfillmentMethod === "local_delivery" ? "Delivery" : "Shipping"}
                                    value={
                                        fulfillmentMethod === "local_delivery"
                                            ? preview?.deliveryFee
                                            : preview?.shippingCost
                                    }
                                />

                                <Divider sx={{ my: 1 }} />

                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "baseline",
                                        py: 1,
                                        px: 1.5,
                                        borderRadius: 2,
                                        bgcolor: "background.default",
                                    }}
                                >
                                    <Typography fontWeight={800}>Total</Typography>
                                    <Typography variant="h6" fontWeight={800}>
                                        ${Number(preview?.total || 0).toFixed(2)}
                                    </Typography>
                                </Box>
                                <SummaryRow label="Paid" value={balance?.paidTotal || 0} />

                                {balanceDue > 0 && (
                                    <Alert severity="warning">
                                        Balance due: ${balanceDue.toFixed(2)}
                                    </Alert>
                                )}

                                {refundDue > 0 && (
                                    <Alert severity="error">
                                        Refund due: ${refundDue.toFixed(2)}
                                    </Alert>
                                )}

                                {preview?.warnings?.map((warning: string) => (
                                    <Alert key={warning} severity="warning">
                                        {warning}
                                    </Alert>
                                ))}

                                {/* Adjustment settlement controls (paid orders) */}
                                {orderPaid && (refundDue > 0 || balanceDue > 0) && (
                                  <Box
                                    sx={{
                                        mt: 1.5,
                                        p: 1.5,
                                        borderRadius: 2,
                                        border: "1px solid", borderColor: "divider",
                                        bgcolor: "background.default",
                                    }}
                                  >
                                    <Typography
                                        variant="caption"
                                        fontWeight={800}
                                        color="text.secondary"
                                        sx={{ display: "block", mb: 1, letterSpacing: 0.6, textTransform: "uppercase" }}
                                    >
                                        Settlement
                                    </Typography>

                                    {refundDue > 0 && (
                                    <FormControl>
                                        <FormLabel sx={{ fontSize: 13, fontWeight: 700 }}>
                                            Refund ${refundDue.toFixed(2)} to
                                        </FormLabel>
                                        <RadioGroup
                                            value={refundDestination}
                                            onChange={(e) =>
                                                setRefundDestination(e.target.value as "manual" | "store_credit")
                                            }
                                        >
                                            <FormControlLabel
                                                value="manual"
                                                control={<Radio size="small" />}
                                                label="Original payment method / cash"
                                                disabled={saving}
                                            />
                                            <FormControlLabel
                                                value="store_credit"
                                                control={<Radio size="small" />}
                                                label="Store credit"
                                                disabled={saving}
                                            />
                                        </RadioGroup>
                                        {["clover", "card", "crypto_eth", "crypto"].includes(
                                            String(order.payment_method || "").toLowerCase()
                                        ) && (
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                                Card and crypto payments can’t be refunded automatically.
                                                “Original method” records the refund here — complete the actual
                                                card refund in Clover. Store credit is applied instantly.
                                            </Typography>
                                        )}
                                    </FormControl>
                                    )}

                                    {balanceDue > 0 && (
                                    <Box sx={{ mt: refundDue > 0 ? 1.5 : 0 }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={collectNow}
                                                    disabled={saving}
                                                    onChange={(e) => setCollectNow(e.target.checked)}
                                                />
                                            }
                                            label={`Collect $${balanceDue.toFixed(2)} now`}
                                        />
                                        {collectNow ? (
                                            <TextField
                                                select
                                                fullWidth
                                                size="small"
                                                label="Payment method"
                                                value={collectMethod}
                                                disabled={saving}
                                                onChange={(e) => setCollectMethod(e.target.value)}
                                                sx={{ mt: 1 }}
                                            >
                                                <MenuItem value="cash">Cash</MenuItem>
                                                <MenuItem value="etransfer">E-transfer</MenuItem>
                                                <MenuItem value="manual_card">Card (manual)</MenuItem>
                                                <MenuItem value="other">Other</MenuItem>
                                            </TextField>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                Left as a balance due — send a payment link later.
                                            </Typography>
                                        )}
                                    </Box>
                                    )}
                                  </Box>
                                )}
                            </Stack>

                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                sx={{ mt: 2 }}
                                disabled={
                                    saving ||
                                    previewing ||
                                    draftItems.length === 0 ||
                                    needsRefundChoice
                                }
                                onClick={handleSave}
                            >
                                {saving
                                    ? "Saving..."
                                    : orderPaid
                                        ? "Save Adjustment"
                                        : "Save Order Changes"}
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
      </Box>
    );
}

function SummaryRow({
    label,
    value,
    strong = false,
}: {
    label: string;
    value: number | undefined;
    strong?: boolean;
}) {
    return (
        <Stack direction="row" justifyContent="space-between">
            <Typography fontWeight={strong ? 800 : 500}>{label}</Typography>
            <Typography fontWeight={strong ? 900 : 600}>
                ${Number(value || 0).toFixed(2)}
            </Typography>
        </Stack>
    );
}